import {
  Input,
  Modal,
  Spin,
  Typography,
  Row,
  Col,
  Tag,
  Space,
  Button,
} from "antd";

import { useEffect, useState } from "react";
import {
  Quantity,
  Target,
  TargetsModalProps,
  useLazyGetOrderTargetsQuery,
  useUpdateOrderTargetsMutation,
} from "shared/api";
import { Colors } from "shared/themes";

const { Title, Text } = Typography;

export const TargetsModal: React.FC<TargetsModalProps> = ({
  isOpen,
  orderId,
  onClose,
  selectedOrderCode,
}) => {
  const [targets, setTargets] = useState<Target[]>([]);
  const [triggerGetTargets, { isFetching }] = useLazyGetOrderTargetsQuery();
  const [updateTargets, { isLoading: isSaving }] =
    useUpdateOrderTargetsMutation();

  const fetchTargets = async (id: number) => {
    try {
      const response = await triggerGetTargets({ orderId: id }).unwrap();
      setTargets(response.targets);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error fetching targets", err);
    }
  };

  useEffect(() => {
    if (orderId && isOpen) {
      fetchTargets(orderId);
    }
  }, [orderId, isOpen]);

  const handleQuantityChange = (
    trackerIdx: number,
    quantityIdx: number,
    field: keyof Quantity,
    value: string
  ) => {
    setTargets((prev) =>
      prev.map((tracker, idx) => {
        if (idx !== trackerIdx) return tracker;

        const updatedQuantities = tracker.quantities.map((q, qIdx) => {
          if (qIdx !== quantityIdx) return q;

          let updatedFieldValue: string | number = value;
          if (field === "count_items" || field === "quantity_total") {
            updatedFieldValue = value === "" ? "" : Number(value);
          }

          return {
            ...q,
            [field]: updatedFieldValue,
          };
        });

        return {
          ...tracker,
          quantities: updatedQuantities,
        };
      })
    );
  };

  const handleTrackerInfoChange = (trackerIdx: number, value: string) => {
    setTargets((prev) =>
      prev.map((tracker, idx) =>
        idx === trackerIdx ? { ...tracker, tracker_info: value } : tracker
      )
    );
  };

  const handleSave = async () => {
    if (!orderId) return;
    try {
      await updateTargets({ orderId, targets }).unwrap();
      onClose();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error saving targets", err);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={isSaving}
      title="Edit Targets"
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isSaving}
          onClick={handleSave}
          style={{ background: `${Colors.blue.primary}` }}
        >
          Save
        </Button>,
      ]}
    >
      {selectedOrderCode && (
        <div className="text-center mb-3">
          <Title level={4}>Order: {selectedOrderCode}</Title>
        </div>
      )}

      {isFetching ? (
        <div className="flex justify-center items-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <div className="space-y-10">
          {targets.map((tracker, trackerIdx) => (
            <div key={tracker.tracker_id}>
              <div>
                <Title level={5}>{tracker.tracker_name}</Title>

                <div>
                  <Text strong>Tracker Info</Text>
                  <Input.TextArea
                    value={
                      tracker.tracker_info === "None"
                        ? ""
                        : tracker.tracker_info
                    }
                    onChange={(e) =>
                      handleTrackerInfoChange(trackerIdx, e.target.value)
                    }
                    placeholder="Enter tracker info (optional)"
                    className="my-2"
                    autoSize
                  />
                </div>
              </div>

              <Space direction="vertical" size="large" className="w-full">
                {tracker.quantities.map((q, quantityIdx) => (
                  <div
                    key={`${q.material_id}-${quantityIdx}`}
                    className="p-4 rounded-md border border-gray-200 bg-gray-50"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <Text type="secondary">Material</Text>
                      <Tag color="blue">{q.material_name}</Tag>
                    </div>

                    <Row gutter={12}>
                      <Col span={12}>
                        <div className="flex flex-col">
                          <Text strong>Item Count</Text>
                          <Input
                            type="number"
                            value={q.count_items}
                            onChange={(e) =>
                              handleQuantityChange(
                                trackerIdx,
                                quantityIdx,
                                "count_items",
                                e.target.value
                              )
                            }
                            placeholder="Enter item count"
                            className="mt-1"
                          />
                        </div>
                      </Col>
                      <Col span={12}>
                        <div className="flex flex-col">
                          <Text strong>Total Quantity</Text>
                          <Input
                            type="number"
                            value={
                              q.quantity_total === "None"
                                ? ""
                                : q.quantity_total
                            }
                            onChange={(e) =>
                              handleQuantityChange(
                                trackerIdx,
                                quantityIdx,
                                "quantity_total",
                                e.target.value
                              )
                            }
                            placeholder="Enter total quantity"
                            className="mt-1"
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>
                ))}
              </Space>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
